const PARTNER_LABELS = {
  gupshup: "Gupshup",
  totalbus: "TotalBus",
  praxxio: "Praxio",
  vuxx: "Vuxx",
  cielo: "Cielo",
  rede: "Rede",
  mercadopago: "MercadoPago",
  itau: "Itaú",
  appmax: "AppMax",
  xpay: "Xpay",
  payment: "Pagamento",
  twilio: "Telefonia",
};

function parseJsonMaybe(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function extractFromObject(obj) {
  if (!obj || typeof obj !== "object") return null;
  if (obj.kind === "partner_api_audit") return obj;
  if (obj.xpro_api_audit && typeof obj.xpro_api_audit === "object") return obj.xpro_api_audit;
  return null;
}

export function resolveMessageApiAudit(message) {
  if (!message) return null;
  if (message.apiAudit && typeof message.apiAudit === "object") return message.apiAudit;

  const fromWa = extractFromObject(parseJsonMaybe(message.json_whatsapp));
  if (fromWa) return fromWa;

  const ackRaw = message.json_status_message_ack;
  if (ackRaw) {
    const ackObj = parseJsonMaybe(ackRaw);
    const fromAck = extractFromObject(ackObj);
    if (fromAck) return fromAck;
    if (typeof ackRaw === "string") {
      const parts = ackRaw.split("} {");
      for (let i = parts.length - 1; i >= 0; i--) {
        const chunk =
          parts.length === 1
            ? parts[0]
            : i === 0
              ? parts[0].trimEnd().endsWith("}")
                ? parts[0]
                : `${parts[0]}}`
              : `{${parts[i]}`;
        const found = extractFromObject(parseJsonMaybe(chunk));
        if (found) return found;
      }
    }
  }
  return null;
}

export function getPartnerDisplayLabel(partner) {
  return PARTNER_LABELS[partner] || String(partner || "API");
}

const TELEPHONY_OPERATION_LABELS = {
  "voice.recording": "Gravação da ligação",
  "voice.status": "Status da ligação",
  "voice.app": "Softphone",
  "voice.outbound": "Ligação de saída",
  "voice.inbound": "Ligação recebida",
  "voice.dial": "Discagem",
  "voice.human": "Atendimento humano",
  "voice.queue": "Fila de voz",
  "video.status": "Status da videochamada",
  "video.composition": "Gravação da videochamada",
};

/**
 * Resumo amigável para timeline/lista (sem marca Twilio / jargão de webhook).
 * Cobre mensagens já gravadas no banco com o texto antigo "API Twilio — …".
 */
export function formatPartnerApiUserFacingSummary(messageOrText, audit = null) {
  const resolved =
    audit ||
    (messageOrText && typeof messageOrText === "object"
      ? resolveMessageApiAudit(messageOrText)
      : null);
  if (resolved && String(resolved.partner || "").toLowerCase() === "twilio") {
    const opKey = String(resolved.operation || "").trim();
    const opLabel =
      TELEPHONY_OPERATION_LABELS[opKey] ||
      (opKey ? opKey.replace(/^voice\./i, "").replace(/[._]/g, " ") : "Atualização de telefonia");
    if (resolved.error) return `Telefonia — ${opLabel} (erro)`;
    return `Telefonia — ${opLabel}`;
  }

  const plain = String(
    typeof messageOrText === "string"
      ? messageOrText
      : messageOrText?.textPlain || messageOrText?.text || ""
  )
    .replace(/<[^>]*>/g, "")
    .trim();
  if (!plain) return "";
  if (/API\s+Twilio\b/i.test(plain) || (/\bTwilio\b/i.test(plain) && /voice\./i.test(plain))) {
    if (/recording/i.test(plain)) return "Telefonia — Gravação da ligação";
    if (/status/i.test(plain)) return "Telefonia — Status da ligação";
    return "Telefonia — Atualização";
  }
  return plain;
}

/** Preview curto na lista de solicitações (mensagens antigas com "API Twilio…"). */
export function sanitizeTelephonyListPreview(text) {
  const plain = String(text || "")
    .replace(/<[^>]*>/g, "")
    .trim();
  if (!plain) return plain;
  if (/API\s+Twilio\b/i.test(plain) || (/\bTwilio\b/i.test(plain) && /voice\./i.test(plain))) {
    if (/recording/i.test(plain)) return "Gravação da ligação";
    if (/status/i.test(plain)) return "Atualização da ligação";
    return "Atualização de telefonia";
  }
  return plain;
}

export function formatAuditJson(value) {
  if (value == null) return "";
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function buildAuditCopyBundle(audit) {
  if (!audit) return "";
  const isWebhook = audit.direction === "inbound_webhook";
  const webhookJson =
    audit.webhookPayload ??
    audit.request?.body ??
    null;
  const lines = [
    `Parceiro: ${getPartnerDisplayLabel(audit.partner)}`,
    `Operação: ${audit.operation || "—"}`,
    `Direção: ${audit.direction || "—"}`,
    audit.response?.status != null ? `HTTP: ${audit.response.status}` : null,
    audit.error ? `Erro: ${audit.error}` : null,
    "",
  ].filter((x) => x !== null);

  if (isWebhook) {
    lines.push("=== JSON RECEBIDO (WEBHOOK) ===", formatAuditJson(webhookJson), "");
    lines.push("=== HEADERS (WEBHOOK) ===", formatAuditJson(audit.request?.headers), "");
    lines.push("=== RESPOSTA DA PLATAFORMA ===", formatAuditJson(audit.response));
  } else {
    lines.push("=== REQUEST ===", formatAuditJson(audit.request), "");
    lines.push("=== RESPONSE ===", formatAuditJson(audit.response));
  }
  return lines.join("\n");
}

export function getWebhookPayloadForDisplay(audit) {
  if (!audit) return null;
  return audit.webhookPayload ?? audit.request?.body ?? null;
}

export function isInboundWebhookAudit(audit) {
  return audit?.direction === "inbound_webhook";
}
